"use client";

import * as Headless from "@headlessui/react";
import React, { useState } from "react";
import { NavbarItem } from "./navbar";
import { useImport } from "../contexts/ImportContext";
import ImportListItem from "../components/ImportListItem";
import {
  ArrowLeftEndOnRectangleIcon,
  Bars2Icon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

function MobileSidebar({
  open,
  close,
  children,
  side = "left", // Default to left
}: React.PropsWithChildren<{
  open: boolean;
  close: () => void;
  side?: "left" | "right";
}>) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <Headless.DialogPanel
        transition
        className={
          `fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out ` +
          (side === "left"
            ? "data-[closed]:-translate-x-full left-0"
            : "data-[closed]:translate-x-full right-0")
        }
      >
        <div className="flex flex-col h-full bg-white rounded-lg shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          <div className="px-4 pt-3 -mb-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <XMarkIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

export function SidebarLayout({
  navbar,
  sidebar,
  rightSidebar,
  children,
}: React.PropsWithChildren<{
  navbar: React.ReactNode;
  sidebar: React.ReactNode;
  rightSidebar: React.ReactNode;
}>) {
  const { imports } = useImport();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);

  return (
    <div className="relative flex w-full bg-white isolate min-h-svh max-lg:flex-col lg:bg-zinc-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
      {/* Sidebar on desktop */}
      <div className="fixed inset-y-0 left-0 w-64 max-lg:hidden">{sidebar}</div>

      {/* Sidebar on mobile */}
      <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
        {sidebar}
      </MobileSidebar>

      {/* Sidebar on mobile */}
      <MobileSidebar
        side="right"
        open={showRightSidebar}
        close={() => setShowRightSidebar(false)}
      >
        {rightSidebar}
      </MobileSidebar>

      {/* Navbar on mobile */}
      <header className="flex items-center px-4 lg:hidden">
        <div className="py-2.5">
          <NavbarItem
            onClick={() => setShowSidebar(true)}
            aria-label="Open navigation"
          >
            <Bars2Icon />
          </NavbarItem>
        </div>
        <div className="flex-1 min-w-0">{navbar}</div>
        {rightSidebar && (
          <NavbarItem
            onClick={() => setShowRightSidebar(true)}
            aria-label="Open navigation"
          >
            <ArrowLeftEndOnRectangleIcon />
          </NavbarItem>
        )}
      </header>

      {/* Content */}
      <main
        className={`flex flex-col flex-1 pb-2 lg:min-w-0 ${rightSidebar ? "lg:pl-64 lg:pr-80" : "lg:pl-64 lg:pr-2"} lg:pt-2`}
      >
        {imports.map((importItem) => (
          <ImportListItem key={importItem._id} importItem={importItem} />
        ))}
        <div className="p-6 grow lg:rounded-lg lg:bg-white lg:p-10 lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10">
          <div className="mx-auto max-w-8xl">{children}</div>
        </div>
      </main>
      {rightSidebar && (
        <div className="fixed inset-y-0 right-0 w-80 max-lg:hidden">
          {rightSidebar}
        </div>
      )}
    </div>
  );
}
